import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { downloadViaAnchor } from 'src/data-capture/file-management/utils/downloadViaAnchor';
import { ApiError } from 'src/error/ApiError';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

type FreshDownloadLink = { url: string; fileName: string };

/**
 * Downloads a presented file through a link signed at the moment of the click.
 *
 * The card's own link is presigned and expires an hour after Siesta made the file, and a chat
 * left open never re-signs it, so following it directly can land on S3's raw error page.
 */
export function useDownloadPresentedFile(conversationId: string) {
  const { mutate, isPending } = useMutation<void, Error, string>({
    mutationFn: cardUrl =>
      downloadViaAnchor(async (url: string) => {
        const { data } = await api.post<FreshDownloadLink>(
          `conversations/${conversationId}/presented-files/download-url`,
          { url },
          // A file that has gone is an ordinary outcome the card explains, not an incident.
          { expectedStatuses: [410] },
        );
        return data;
      }, cardUrl),
    onError: error => {
      // 410 means Siesta's copy of the file has gone from S3, so a retry can never work.
      if (error instanceof ApiError && error.status === 410) {
        notifications.show({
          title: 'File no longer available',
          message: 'Ask Siesta to produce the file again.',
          color: 'red',
        });
        return;
      }
      notifyAndLogError('Could not download the file', 'Please try again.', error, {
        conversationId,
      });
    },
  });

  return { downloadPresentedFile: mutate, isDownloading: isPending };
}
